import React, { useState, useEffect } from 'react';
import { Bell, Check, CheckCircle2, MessageSquare, AlertCircle, RefreshCw, Layers, Clock, Eye, CalendarDays } from 'lucide-react';
import { PageHeader, Card, Button, Loading } from '../components/ui';
import { notificationsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Helper function to check if a date is today
const isToday = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
};

// Helper to format date and time (e.g. Aug 6, 2026, 10:45 AM)
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${hours}:${minutes}:${String(d.getSeconds()).padStart(2, '0')} ${ampm}`;
};

export default function Notifications() {
  const { user: me } = useAuth();
  const [filter, setFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all'); // all, weekly, monthly, yearly
  const [toggles, setToggles] = useState({
    status: true,
    comments: true,
    deadlines: true
  });
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load preferences from localStorage when user profile loads
  useEffect(() => {
    if (me?.user_id) {
      const saved = localStorage.getItem(`notif_prefs_${me.user_id}`);
      if (saved) {
        try {
          setToggles(JSON.parse(saved));
        } catch (e) {
          console.error('Error loading notification preferences:', e);
        }
      }
    }
  }, [me]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = me?.role === 'Admin' 
        ? await notificationsAPI.getAll() 
        : await notificationsAPI.list();
      setNotifications(res.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (me) {
      fetchNotifications();
    }
  }, [me]);

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAll();
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleMarkRead = async (id, e) => {
    if(e) e.stopPropagation();
    try {
      await notificationsAPI.markRead(id);
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark read:', err);
    }
  };

  const toggleSetting = (key) => {
    setToggles(prev => {
      const next = { ...prev, [key]: !prev[key] };
      if (me?.user_id) {
        localStorage.setItem(`notif_prefs_${me.user_id}`, JSON.stringify(next));
      }
      return next;
    });
  };

  // Helper to customize text for Admin role (shows target user name instead of "you")
  const getNotificationDetails = (n) => {
    let title = n.title || '';
    let message = n.message || '';
    
    // If Admin is viewing all system notifications, show who it was assigned/sent to
    if (me?.role === 'Admin' && n.user) {
      const userName = n.user.name;
      title = title.replace(/to you/i, `to ${userName}`);
      title = title.replace(/assigned to you/i, `assigned to ${userName}`);
      message = message.replace(/You have been/i, `${userName} has been`);
      message = message.replace(/you have been/i, `${userName} has been`);
      message = message.replace(/You were/i, `${userName} was`);
      message = message.replace(/you/i, userName);
    }
    
    return { title, message };
  };

  // Filtering by category, time range and user toggles
  const filteredNotifs = notifications.filter(n => {
    const title = (n.title || '').toLowerCase();
    const type = (n.type || '').toLowerCase();
    
    // Check against notification preference toggles
    const isStatus = type.includes('status') || title.includes('status') || title.includes('assigned');
    const isComment = type.includes('comment') || title.includes('comment') || title.includes('message');
    const isDeadline = type.includes('deadline') || title.includes('due');
    
    if (isStatus && !toggles.status) return false;
    if (isComment && !toggles.comments) return false;
    if (isDeadline && !toggles.deadlines) return false;

    // 1. Category Filter
    let categoryMatch = true;
    if (filter !== 'all') {
      if (filter === 'status') {
        categoryMatch = isStatus;
      } else if (filter === 'comments') {
        categoryMatch = isComment;
      } else if (filter === 'deadlines') {
        categoryMatch = isDeadline;
      } else {
        categoryMatch = false;
      }
    }
    
    // 2. Time Range Filter
    let timeMatch = true;
    if (timeFilter !== 'all') {
      const createdDate = new Date(n.created_at || n.createdAt);
      const diffTime = Math.abs(new Date() - createdDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (timeFilter === 'weekly') timeMatch = diffDays <= 7;
      else if (timeFilter === 'monthly') timeMatch = diffDays <= 30;
      else if (timeFilter === 'yearly') timeMatch = diffDays <= 365;
    }

    return categoryMatch && timeMatch;
  });

  const todayNotifs = filteredNotifs.filter(n => isToday(n.created_at || n.createdAt));
  const earlierNotifs = filteredNotifs.filter(n => !isToday(n.created_at || n.createdAt));
  const unreadCount = notifications.filter(n => !n.read).length;
  const myUnreadCount = notifications.filter(n => !n.read && Number(n.user_id) === Number(me?.user_id)).length;

  const getIconForNotification = (n) => {
    const title = (n.title || '').toLowerCase();
    if (title.includes('comment') || title.includes('message')) return <MessageSquare size={18} />;
    if (title.includes('deadline') || title.includes('due')) return <AlertCircle size={18} />;
    if (title.includes('status') || title.includes('moved') || title.includes('assigned')) return <RefreshCw size={18} />;
    return <CheckCircle2 size={18} />;
  };

  const getIconColorStyle = (n) => {
    const title = (n.title || '').toLowerCase();
    if (title.includes('comment') || title.includes('message')) return { bg: 'rgba(167,139,250,0.1)', color: '#a78bfa' };
    if (title.includes('deadline') || title.includes('due')) return { bg: 'rgba(239,68,68,0.1)', color: '#ef4444' };
    if (title.includes('status') || title.includes('moved') || title.includes('assigned')) return { bg: 'rgba(56,189,248,0.1)', color: '#38bdf8' };
    return { bg: 'rgba(52,211,153,0.1)', color: '#34d399' };
  };

  return (
    <div className="animate-in" style={{ color: 'var(--text)' }}>
      
      {/* Header */}
      <PageHeader 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(245,166,35,0.2), rgba(245,166,35,0.05))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', border: '1px solid rgba(245,166,35,0.3)' }}>
              <Bell size={20} />
            </div>
            Notifications
          </div>
        }
        subtitle={me?.role === 'Admin' ? `${unreadCount} total unread` : `${unreadCount} unread`}
        action={
          <Button variant="secondary" onClick={handleMarkAllRead} style={{ display: 'flex', gap: '8px', alignItems: 'center' }} disabled={myUnreadCount === 0 || loading}>
            <Check size={16} /> Mark all read
          </Button>
        }
      />

      {/* Filters toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        
        {/* Category Filters */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { id: 'all', label: 'All' },
            { id: 'status', label: 'Task status' },
            { id: 'comments', label: 'Comments & Messages' },
            { id: 'deadlines', label: 'Deadlines' }
          ].map(f => (
            <button 
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{ 
                background: filter === f.id ? 'var(--accent)' : 'var(--bg-card)', 
                color: filter === f.id ? '#000' : 'var(--text-muted)', 
                border: filter === f.id ? '1px solid var(--accent)' : '1px solid var(--border)',
                padding: '6px 14px', borderRadius: '24px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: filter === f.id ? '0 4px 12px rgba(245,166,35,0.2)' : 'none'
              }}
            >
              {f.label} 
            </button>
          ))}
        </div>

        {/* Time Filters */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', background: 'var(--bg-card)', padding: '4px', borderRadius: '20px', border: '1px solid var(--border)' }}>
          {[
            { id: 'all', label: 'All Time' },
            { id: 'weekly', label: 'Weekly' },
            { id: 'monthly', label: 'Monthly' },
            { id: 'yearly', label: 'Yearly' }
          ].map(tf => (
            <button
              key={tf.id}
              onClick={() => setTimeFilter(tf.id)}
              style={{
                background: timeFilter === tf.id ? 'var(--bg-hover)' : 'transparent',
                color: timeFilter === tf.id ? 'var(--text)' : 'var(--text-muted)',
                border: 'none',
                padding: '4px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              {tf.id === 'all' ? null : <CalendarDays size={12} />}
              {tf.label}
            </button>
          ))}
        </div>

      </div>

      {loading ? (
        <Card style={{ padding: '40px', display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
          <Loading />
        </Card>
      ) : (
        <>
          {/* Scrollable Feed Container */}
          <div 
            style={{ 
              maxHeight: '520px', 
              overflowY: 'auto', 
              paddingRight: '8px', 
              marginBottom: '32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}
            className="custom-scrollbar"
          >
            {/* TODAY Section */}
            {todayNotifs.length > 0 && (
              <div style={{ animation: 'slideIn 0.4s ease forwards' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>TODAY</div>
                  <div style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {todayNotifs.map(n => {
                    const style = getIconColorStyle(n);
                    const isUnread = !n.read;
                    const isOwnNotification = Number(n.user_id) === Number(me?.user_id);
                    const { title, message } = getNotificationDetails(n);
                    return (
                      <Card 
                        key={n.notification_id || n.id}
                        style={{ 
                          padding: '20px 24px', margin: 0, display: 'flex', alignItems: 'center', gap: '20px', 
                          borderLeft: `3px solid ${isUnread ? style.color : 'var(--border)'}`,
                          background: isUnread ? `linear-gradient(90deg, ${style.bg.replace('0.1', '0.05')}, transparent)` : 'var(--bg-card)'
                        }}
                      >
                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: style.bg, color: style.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {getIconForNotification(n)}
                        </div>
                        
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '16px', fontWeight: isUnread ? '700' : '600', color: 'var(--text)' }}>{title}</span>
                            {isUnread && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: style.color, boxShadow: `0 0 8px ${style.color}` }}></span>}
                          </div>
                          
                          <div style={{ fontSize: '14.5px', color: 'var(--text-muted)', lineHeight: '1.5' }}>{message}</div>
                          
                          {/* Dates Metadata row */}
                          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 500 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Clock size={13} />
                              <span>Sent: {formatDate(n.created_at || n.createdAt)}</span>
                            </div>
                            {!isUnread && (n.updatedAt || n.updated_at) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#34d399' }}>
                                <Eye size={13} />
                                <span>Read At: {formatDate(n.updatedAt || n.updated_at)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Clean Right Actions Container */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px', flexShrink: 0 }}>
                          <span style={{ 
                            fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '6px',
                            background: isUnread ? 'rgba(245,166,35,0.1)' : 'rgba(52,211,153,0.1)',
                            color: isUnread ? 'var(--accent)' : '#34d399',
                            border: isUnread ? '1px solid rgba(245,166,35,0.2)' : '1px solid rgba(52,211,153,0.2)'
                          }}>
                            {isUnread ? 'Unread' : 'Read'}
                          </span>
                          
                          {/* Mark Read option only shown if it's the user's own unread notification */}
                          {isUnread && isOwnNotification && (
                            <Button 
                              onClick={(e) => handleMarkRead(n.notification_id || n.id, e)} 
                              variant="secondary" 
                              size="sm" 
                              style={{ 
                                padding: '5px 10px', 
                                display: 'flex', 
                                gap: '6px', 
                                alignItems: 'center',
                                border: '1px solid var(--accent)',
                                color: 'var(--accent)',
                                fontSize: '12px'
                              }}
                            >
                              <CheckCircle2 size={13} /> Mark Read
                            </Button>
                          )}
                        </div>
                      </Card>
                    )
                  })}
                </div>
              </div>
            )}

            {/* EARLIER Section */}
            {earlierNotifs.length > 0 && (
              <div style={{ animation: 'slideIn 0.5s ease forwards' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>EARLIER</div>
                  <div style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {earlierNotifs.map(n => {
                    const style = getIconColorStyle(n);
                    const isUnread = !n.read;
                    const isOwnNotification = Number(n.user_id) === Number(me?.user_id);
                    const { title, message } = getNotificationDetails(n);
                    return (
                      <Card 
                        key={n.notification_id || n.id}
                        style={{ 
                          padding: '20px 24px', margin: 0, display: 'flex', alignItems: 'center', gap: '16px', 
                          opacity: isUnread ? 1 : 0.75, 
                          borderLeft: `3px solid ${isUnread ? style.color : 'transparent'}`,
                        }}
                      >
                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: style.bg, color: style.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {getIconForNotification(n)}
                        </div>
                        
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '16px', fontWeight: isUnread ? '700' : '500', color: 'var(--text)' }}>{title}</span>
                            {isUnread && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: style.color, boxShadow: `0 0 8px ${style.color}` }}></span>}
                          </div>
                          
                          <div style={{ fontSize: '14.5px', color: 'var(--text-muted)', lineHeight: '1.5' }}>{message}</div>
                          
                          {/* Dates Metadata row */}
                          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 500 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Clock size={13} />
                              <span>Sent: {formatDate(n.created_at || n.createdAt)}</span>
                            </div>
                            {!isUnread && (n.updatedAt || n.updated_at) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#34d399' }}>
                                <Eye size={13} />
                                <span>Read At: {formatDate(n.updatedAt || n.updated_at)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Clean Right Actions Container */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px', flexShrink: 0 }}>
                          <span style={{ 
                            fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '6px',
                            background: isUnread ? 'rgba(245,166,35,0.1)' : 'rgba(52,211,153,0.05)',
                            color: isUnread ? 'var(--accent)' : 'var(--text-muted)',
                            border: isUnread ? '1px solid rgba(245,166,35,0.2)' : '1px solid var(--border)'
                          }}>
                            {isUnread ? 'Unread' : 'Read'}
                          </span>
                          
                          {/* Mark Read option only shown if it's the user's own unread notification */}
                          {isUnread && isOwnNotification && (
                            <Button 
                              onClick={(e) => handleMarkRead(n.notification_id || n.id, e)} 
                              variant="secondary" 
                              size="sm" 
                              style={{ 
                                padding: '5px 10px', 
                                display: 'flex', 
                                gap: '6px', 
                                alignItems: 'center',
                                border: '1px solid var(--accent)',
                                color: 'var(--accent)',
                                fontSize: '12px'
                              }}
                            >
                              <CheckCircle2 size={13} /> Mark Read
                            </Button>
                          )}
                        </div>
                      </Card>
                    )
                  })}
                </div>
              </div>
            )}

            {filteredNotifs.length === 0 && (
              <Card style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', margin: 0 }}>
                No notifications found matching your filters.
              </Card>
            )}
          </div>
        </>
      )}

      {/* Settings Section */}
      <Card style={{ margin: 0, padding: '32px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '150px', height: '150px', background: 'var(--accent)', filter: 'blur(80px)', opacity: 0.1 }}></div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <div style={{ padding: '8px', background: 'var(--bg)', borderRadius: '8px', color: 'var(--accent)', border: '1px solid var(--border-bright)' }}>
            <Layers size={18} />
          </div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', fontFamily: 'var(--font-display)' }}>Notification Preferences</h3>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>Task status changes</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Get notified when a task moves across the board.</div>
            </div>
            <div 
              onClick={() => toggleSetting('status')}
              style={{ 
                width: '44px', height: '24px', 
                background: toggles.status ? 'var(--accent)' : 'var(--bg-elevated)', 
                borderRadius: '12px', display: 'flex', alignItems: 'center', padding: '2px', cursor: 'pointer', 
                transition: 'all 0.3s ease', border: toggles.status ? '1px solid var(--accent)' : '1px solid var(--border-bright)' 
              }}>
              <div style={{ 
                width: '18px', height: '18px', background: toggles.status ? '#000' : 'var(--text-muted)', 
                borderRadius: '50%', transform: toggles.status ? 'translateX(20px)' : 'translateX(0)', 
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' 
              }}></div>
            </div>
          </div>
          
          <div style={{ height: '1px', background: 'var(--border)' }}></div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>New comments</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Get notified when team members mention you.</div>
            </div>
            <div 
              onClick={() => toggleSetting('comments')}
              style={{ 
                width: '44px', height: '24px', 
                background: toggles.comments ? 'var(--accent)' : 'var(--bg-elevated)', 
                borderRadius: '12px', display: 'flex', alignItems: 'center', padding: '2px', cursor: 'pointer', 
                transition: 'all 0.3s ease', border: toggles.comments ? '1px solid var(--accent)' : '1px solid var(--border-bright)' 
              }}>
              <div style={{ 
                width: '18px', height: '18px', background: toggles.comments ? '#000' : 'var(--text-muted)', 
                borderRadius: '50%', transform: toggles.comments ? 'translateX(20px)' : 'translateX(0)', 
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' 
              }}></div>
            </div>
          </div>
          
          <div style={{ height: '1px', background: 'var(--border)' }}></div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>Deadlines approaching</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Get notified 24 hours before a project is due.</div>
            </div>
            <div 
              onClick={() => toggleSetting('deadlines')}
              style={{ 
                width: '44px', height: '24px', 
                background: toggles.deadlines ? 'var(--accent)' : 'var(--bg-elevated)', 
                borderRadius: '12px', display: 'flex', alignItems: 'center', padding: '2px', cursor: 'pointer', 
                transition: 'all 0.3s ease', border: toggles.deadlines ? '1px solid var(--accent)' : '1px solid var(--border-bright)' 
              }}>
              <div style={{ 
                width: '18px', height: '18px', background: toggles.deadlines ? '#000' : 'var(--text-muted)', 
                borderRadius: '50%', transform: toggles.deadlines ? 'translateX(20px)' : 'translateX(0)', 
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' 
              }}></div>
            </div>
          </div>

        </div>
      </Card>

    </div>
  );
}
